// apps/client/src/hooks/useAnimatedCounter.ts
//
// Odometer-style animated counter hook — the "Dopamine Odometer".
// Smoothly animates from previous value to new value over 1.5 seconds.
// Used for BingoChips balance everywhere.
//

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAnimatedCounterOptions {
    /** Animation duration in ms. Default 1500. */
    duration?: number;
    /** Start value. Default 0. */
    initialValue?: number;
}

export function useAnimatedCounter(
    targetValue: number,
    options: UseAnimatedCounterOptions = {}
) {
    const { duration = 1500, initialValue = 0 } = options;

    const [displayValue, setDisplayValue] = useState(initialValue);
    const animationRef = useRef<number | null>(null);
    const startValueRef = useRef(initialValue);
    const startTimeRef = useRef<number | null>(null);

    // Easing function: ease-out cubic
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = useCallback(
        (timestamp: number) => {
            if (startTimeRef.current === null) {
                startTimeRef.current = timestamp;
            }

            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOutCubic(progress);

            const currentValue = Math.round(
                startValueRef.current + (targetValue - startValueRef.current) * easedProgress
            );

            setDisplayValue(currentValue);

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
        },
        [targetValue, duration]
    );

    useEffect(() => {
        // Cancel any existing animation
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        // Store current display value as start of new animation
        startValueRef.current = displayValue;
        startTimeRef.current = null;

        // Start animation
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
        // Only re-run when target changes, not when displayValue changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetValue, animate]);

    return displayValue;
}
